import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { API } from "@/auth/AuthContext";
import { inputCls, labelCls } from "@/components/formkit";

const IMG = {
  hero: "https://images.unsplash.com/photo-1509391366360-2e959784a276?q=85&w=1600&auto=format&fit=crop",
  rooftop: "https://images.unsplash.com/photo-1613665813446-82a78c468a1d?q=85&w=1200&auto=format&fit=crop",
  commercial: "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?q=85&w=1200&auto=format&fit=crop",
};

const SERVICE_OPTIONS = [
  "Residential Solar Installation", "Commercial Solar Installation", "Rooftop Solar Panel Mounting",
  "Ground-Mounted Solar Installation", "Solar Panel Expansion", "Solar Maintenance",
  "Solar & Smart Home Integration", "Solar + Local AI Consultation",
];

const SERVICES = [
  {
    name: "Residential Solar Installation",
    desc: "Professional solar panel installation for residential properties.",
    items: ["Rooftop solar panel mounting", "Solar racking installation", "Panel positioning",
      "Mounting hardware installation", "Mechanical installation support",
      "Electrical contractor coordination", "System activation coordination"],
    cta: "Request Solar Quote", service: "Residential Solar Installation",
  },
  {
    name: "Commercial Solar Installation",
    desc: "Solar panel mounting and installation support for commercial properties.",
    items: ["Commercial rooftop solar mounting", "Ground-mounted solar arrays", "Solar racking installation",
      "Panel installation", "Installation crew support", "Electrical contractor coordination",
      "Project installation management"],
    cta: "Request Commercial Quote", service: "Commercial Solar Installation",
  },
  {
    name: "Solar Panel Expansion",
    desc: "Installation support for customers looking to expand existing solar systems.",
    items: ["Additional panel mounting", "Solar racking expansion", "Equipment installation support",
      "Existing system assessment", "Electrical integration coordination"],
    cta: "Request Custom Quote", service: "Solar Panel Expansion",
  },
  {
    name: "Solar System Maintenance",
    desc: "Installation-related maintenance and inspection services.",
    items: ["Visual panel inspections", "Mounting hardware inspections", "Solar racking assessments",
      "Panel replacement support", "Solar equipment inspection", "Electrical service coordination"],
    cta: "Request Service", service: "Solar Maintenance",
  },
];

const STEPS = [
  { n: "01", title: "Solar Consultation", desc: "We evaluate your project, installation requirements, and equipment needs." },
  { n: "02", title: "Professional Panel Installation", desc: "DuroTech28 performs solar panel mounting and mechanical installation work within its authorized scope." },
  { n: "03", title: "Electrical Connection & Activation", desc: "Appropriately licensed electrical contractors complete regulated electrical work. Required inspections, utility approvals, and permission to operate are obtained before activation." },
];

function FeatureList({ items }) {
  return (
    <ul className="space-y-2 text-xs text-zinc-300 font-mono">
      {items.map((i) => (
        <li key={i} className="flex items-start gap-2"><span className="text-[#d4af37] mt-0.5">▶</span> {i}</li>
      ))}
    </ul>
  );
}

function SolarForm({ service, setService }) {
  const EMPTY = {
    name: "", phone: "", email: "", project_address: "", property_type: "Residential",
    roof_type: "", panel_count: "", existing_solar: "", description: "", preferred_date: "",
  };
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries({ ...form, service }).forEach(([k, v]) => fd.append(k, v));
      photos.slice(0, 5).forEach((p) => fd.append("photos", p));
      await axios.post(`${API}/applications/solar`, fd);
      setDone(true);
      setForm(EMPTY);
      setPhotos([]);
      toast.success("Solar inquiry received — DuroTech28 will reach out shortly.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit right now. Please call 469-897-6497.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-10" data-testid="solar-success">
        <div className="w-14 h-14 mx-auto mb-5 rounded-full border border-[#d4af37]/50 flex items-center justify-center">
          <svg className="w-7 h-7 text-[#d4af37]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </div>
        <p className="text-sm text-zinc-200 leading-relaxed max-w-md mx-auto">
          Thank you for your interest in DuroTech28 solar services. Your request has been received —
          our team will review your project and contact you to schedule a consultation.
        </p>
        <button type="button" onClick={() => setDone(false)} data-testid="solar-reset"
          className="mt-6 px-6 py-2.5 rounded border border-[#d4af37]/40 text-[10px] font-mono uppercase tracking-wider text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors">
          Submit Another Request
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="solar-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className={labelCls}>Customer Name *</label>
          <input required value={form.name} onChange={set("name")} data-testid="solar-name" className={inputCls} /></div>
        <div><label className={labelCls}>Phone Number *</label>
          <input required value={form.phone} onChange={set("phone")} data-testid="solar-phone" className={inputCls} /></div>
        <div><label className={labelCls}>Email Address *</label>
          <input required type="email" value={form.email} onChange={set("email")} data-testid="solar-email" className={inputCls} /></div>
        <div><label className={labelCls}>Project Address</label>
          <input value={form.project_address} onChange={set("project_address")} data-testid="solar-address" className={inputCls} /></div>
        <div><label className={labelCls}>Service Needed</label>
          <select value={service} onChange={(e) => setService(e.target.value)} data-testid="solar-service-select" className={inputCls}>
            {SERVICE_OPTIONS.map((s) => <option key={s}>{s}</option>)}
          </select></div>
        <div>
          <span className={labelCls}>Residential or Commercial</span>
          <div className="flex gap-2">
            {["Residential", "Commercial"].map((v) => (
              <button key={v} type="button"
                onClick={() => setForm((f) => ({ ...f, property_type: v }))}
                data-testid={`solar-type-${v.toLowerCase()}`}
                className={`px-5 py-2.5 rounded border text-[11px] font-mono transition-colors ${
                  form.property_type === v
                    ? "bg-[#d4af37]/15 border-[#d4af37] text-[#d4af37]"
                    : "bg-[#0a0a0c] border-zinc-700 text-zinc-400 hover:border-zinc-500"
                }`}>
                {v}
              </button>
            ))}
          </div>
        </div>
        <div><label className={labelCls}>Roof Type (if known)</label>
          <input value={form.roof_type} onChange={set("roof_type")} placeholder="e.g. asphalt shingle, tile, flat" data-testid="solar-roof" className={inputCls} /></div>
        <div><label className={labelCls}>Number of Panels (if known)</label>
          <input value={form.panel_count} onChange={set("panel_count")} data-testid="solar-panels" className={inputCls} /></div>
      </div>
      <div>
        <span className={labelCls}>Existing Solar System?</span>
        <div className="flex gap-2">
          {["yes", "no"].map((v) => (
            <button key={v} type="button"
              onClick={() => setForm((f) => ({ ...f, existing_solar: v }))}
              data-testid={`solar-existing-${v}`}
              className={`px-5 py-2 rounded border text-[11px] font-mono uppercase transition-colors ${
                form.existing_solar === v
                  ? "bg-[#d4af37]/15 border-[#d4af37] text-[#d4af37]"
                  : "bg-[#0a0a0c] border-zinc-700 text-zinc-400 hover:border-zinc-500"
              }`}>
              {v}
            </button>
          ))}
        </div>
      </div>
      <div><label className={labelCls}>Project Description</label>
        <textarea rows="4" value={form.description} onChange={set("description")} data-testid="solar-description" className={inputCls}></textarea></div>
      <div>
        <span className={labelCls}>Photos (optional — up to 5)</span>
        <label className="flex items-center justify-center w-full border border-dashed border-zinc-600 hover:border-[#d4af37] rounded p-4 text-[11px] font-mono text-zinc-400 cursor-pointer transition-colors" data-testid="solar-photos">
          <input type="file" accept="image/*,.pdf" multiple className="hidden"
            onChange={(e) => setPhotos(Array.from(e.target.files || []))} />
          {photos.length ? <span className="text-[#d4af37]">{photos.length} file(s) attached</span> : "Tap to attach site or roof photos"}
        </label>
      </div>
      <div><label className={labelCls}>Preferred Consultation Date</label>
        <input value={form.preferred_date} onChange={set("preferred_date")} data-testid="solar-date" className={inputCls} /></div>
      <button type="submit" disabled={submitting} data-testid="solar-submit"
        className="w-full py-4 rounded bg-gradient-to-r from-[#d4af37] to-[#e6ca65] text-black font-bold uppercase tracking-wider text-xs hover:opacity-95 transition-opacity shadow-lg shadow-[#d4af37]/25 disabled:opacity-60">
        {submitting ? "Submitting..." : "Request Solar Installation Quote"}
      </button>
      <p className="text-[10px] text-zinc-500 font-mono text-center leading-relaxed">
        Custom quote only. All projects comply with applicable permitting, structural, roofing, electrical, and utility requirements.
      </p>
    </form>
  );
}

export default function SolarPage() {
  const [service, setService] = useState(SERVICE_OPTIONS[0]);
  const goQuote = (svc) => {
    setService(svc);
    document.querySelector("#solar-quote")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <Seo
        title="Solar Installation Support | DuroTech28 — DFW"
        description="Residential and commercial solar installation support across Dallas–Fort Worth, coordinated with licensed partners. Request a free solar quote from DuroTech28."
        canonical="https://durotech28.me/solar-installation"
      />
      <Nav />
      <main data-testid="solar-page">
        {/* Hero */}
        <header className="relative overflow-hidden border-b border-[#d4af37]/15">
          <img src={IMG.hero} alt="Residential rooftop solar panel installation" className="absolute inset-0 w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0c]/60 via-[#0a0a0c]/80 to-[#0a0a0c]"></div>
          <div className="relative max-w-5xl mx-auto px-6 pt-20 pb-16 text-center">
            <span className="text-xs uppercase tracking-widest text-[#d4af37] font-mono">Solar Panel Installation · Dallas–Fort Worth</span>
            <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-tight text-white mt-4 leading-[0.98]">
              Power Your Future.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d4af37] via-[#fff4cc] to-[#c0c0c0]">Install With Confidence.</span>
            </h1>
            <p className="text-zinc-300 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed mt-6">
              Professional solar panel mounting and installation for residential and commercial properties —
              from rooftop mounting to ground-mounted arrays — coordinated with licensed electrical
              professionals for connections, commissioning, and activation.
            </p>
            <button type="button" onClick={() => goQuote(SERVICE_OPTIONS[0])} data-testid="solar-hero-btn"
              className="mt-8 px-8 py-4 rounded bg-gradient-to-r from-[#d4af37] to-[#e6ca65] text-black font-bold uppercase tracking-wider text-sm hover:opacity-95 transition-opacity shadow-lg shadow-[#d4af37]/25">
              Request Solar Quote →
            </button>
          </div>
        </header>

        {/* Service cards */}
        <section className="max-w-6xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-2 gap-6">
            {SERVICES.map((s) => (
              <div key={s.name} className="rounded-2xl p-7 bg-[#121216] gold-border flex flex-col"
                data-testid={`solar-card-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <h2 className="font-display text-xl uppercase text-white">{s.name}</h2>
                <p className="text-xs text-zinc-400 mt-2 mb-4">{s.desc}</p>
                <FeatureList items={s.items} />
                <div className="flex-1" />
                <p className="font-display text-lg text-[#d4af37] mt-5">CUSTOM QUOTE</p>
                <button type="button" onClick={() => goQuote(s.service)}
                  data-testid={`solar-card-btn-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="mt-3 w-full py-3 rounded border border-[#d4af37]/40 text-[#d4af37] font-bold uppercase tracking-wider text-xs hover:bg-[#d4af37] hover:text-black transition-colors">
                  {s.cta}
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Licensed partners / 3 steps */}
        <section className="bg-[#0e0e12] border-y border-[#d4af37]/15 py-16" data-testid="solar-partners">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tight text-white">Complete Solar Installation Support</h2>
              <p className="text-xs text-zinc-400 leading-relaxed mt-4">
                A successful solar installation requires more than mounting panels. DuroTech28 coordinates
                with licensed electrical professionals to support projects requiring electrical connections,
                inverter installation, system testing, and utility interconnection — one coordinated project process.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {STEPS.map((s) => (
                <div key={s.n} className="rounded-xl bg-[#121216] gold-border p-6" data-testid={`solar-step-${s.n}`}>
                  <span className="font-display text-3xl text-[#d4af37]/40">{s.n}</span>
                  <h3 className="font-display text-base uppercase text-white mt-3">{s.title}</h3>
                  <p className="text-xs text-zinc-400 leading-relaxed mt-2">{s.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-zinc-500 font-mono text-center mt-8 leading-relaxed max-w-2xl mx-auto">
              Regulated electrical work is completed by appropriately licensed electrical contractors.
              Utility approval and activation timelines vary by project and provider.
            </p>
          </div>
        </section>

        {/* Solar + smart tech */}
        <section className="max-w-6xl mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="rounded-xl overflow-hidden gold-border aspect-[4/3] bg-[#121216] relative">
              <img src={IMG.rooftop} alt="Modern solar-powered home with rooftop panels" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/10"></div>
            </div>
            <div>
              <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tight text-white">Solar Meets Smart Technology</h2>
              <p className="text-sm text-zinc-400 leading-relaxed mt-4">
                DuroTech28 goes beyond traditional solar panel mounting — we help you explore integrating
                compatible solar monitoring systems with your smart home technology.
              </p>
              <div className="mt-5">
                <FeatureList items={[
                  "Solar energy monitoring", "Smart energy dashboards", "Home energy consumption tracking",
                  "Smart thermostat integration", "Compatible battery monitoring",
                  "Intelligent appliance scheduling", "Local AI energy management",
                ]} />
              </div>
              <p className="text-[10px] text-zinc-500 font-mono mt-4 leading-relaxed">
                Advanced automation requires compatible equipment and custom integration.
              </p>
              <button type="button" onClick={() => goQuote("Solar & Smart Home Integration")} data-testid="solar-smart-btn"
                className="mt-6 px-6 py-3 rounded bg-gradient-to-r from-[#d4af37] to-[#e6ca65] text-black font-bold uppercase tracking-wider text-xs hover:opacity-95 transition-opacity">
                Explore Smart Energy Solutions
              </button>
            </div>
          </div>
        </section>

        {/* Quote form */}
        <section id="solar-quote" className="max-w-3xl mx-auto px-6 pb-20 scroll-mt-24" data-testid="solar-quote-section">
          <div className="rounded-2xl p-6 sm:p-10 bg-gradient-to-b from-[#14141a] to-[#0d0d10] border border-[#d4af37]/30">
            <div className="text-center mb-8">
              <span className="text-xs font-mono uppercase tracking-widest text-[#d4af37]">Free Consultation · Custom Quote Only</span>
              <h2 className="font-display text-3xl uppercase text-white mt-2">Request Your Solar Quote</h2>
            </div>
            <SolarForm service={service} setService={setService} />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
